"use client";

import type React from "react";

import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { TagsOutput, PaginatedResponse, Pagination } from "@/interfaces";
import { TagsService } from "@/services/TagService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
  PlusCircle,
  Search,
  Trash,
  Tag,
  Edit,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Tags = () => {
  const [tags, setTags] = useState<TagsOutput[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    total: 0,
    pages: 1,
    links: {
      self: "",
    },
  });
  const navigate = useNavigate();
  // Remover searchParams

  const tagsService = new TagsService();

  // Hook para gerenciar a confirmação de exclusão
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  // Referência para controlar chamadas duplicadas
  const isFetchingRef = useRef(false);

  // Remover a função getParamsFromUrl e os useEffect relacionados à URL

  // Modificar a função fetchTags para usar as interfaces importadas
  const fetchTags = async (query?: string, page = 1, size = 10) => {
    // Evitar chamadas duplicadas
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const queryParams: Record<string, string | number | string[]> = {
        page,
        pageSize: size,
      };

      if (query) {
        queryParams.query = query;
      }

      const response: PaginatedResponse<TagsOutput> = await tagsService.findAll(
        queryParams
      );

      // Usar o novo formato de resposta paginada
      setTags(response.data);

      // Verificar se a página atual é válida
      if (
        response.pagination.pages > 0 &&
        pagination.page > response.pagination.pages
      ) {
        // Se a página atual for maior que o número total de páginas, redirecionar para a última página
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.pages,
        }));
      } else {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Erro ao buscar tags:", error);
      setTags([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        pages: 1,
      }));

      // Se ocorrer um erro, voltar para a página 1
      if (pagination.page !== 1) {
        setPagination((prev) => ({
          ...prev,
          page: 1,
        }));
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const handleNavigateToForm = (tagId?: string) => {
    const targetRoute = tagId ? `/tags/${tagId}/editar` : "/tags/novo";
    navigate(targetRoute);
  };

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    fetchTags(searchTerm, pagination.page, pagination.pageSize);
  }, []);

  // Atualizar quando a paginação mudar
  useEffect(() => {
    fetchTags(searchTerm, pagination.page, pagination.pageSize);
  }, [pagination.page, pagination.pageSize]);

  const handleDeleteClick = (tagId: string) => {
    openDeleteDialog(tagId);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setIsLoading(true);
      await tagsService.delete(itemToDelete);

      // Após excluir, verificar se a página atual ainda tem itens
      // Se não tiver e não for a primeira página, voltar para a página anterior
      if (tags.length === 1 && pagination.page > 1) {
        setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
      } else {
        // Recarregar a página atual
        fetchTags(searchTerm, pagination.page, pagination.pageSize);
      }
    } finally {
      setIsLoading(false);
      closeDeleteDialog();
    }
  };

  // Função para filtrar as tags com base no termo de busca
  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 })); // Resetar para a primeira página ao filtrar
    fetchTags(searchTerm, 1, pagination.pageSize);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handlePageSizeChange = (value: string) => {
    const newSize = Number.parseInt(value);
    setPagination((prev) => ({
      ...prev,
      pageSize: newSize,
      page: 1, // Resetar para a primeira página ao mudar o tamanho
    }));
  };

  if (isLoading && tags.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Calcular o intervalo de itens exibidos
  const startItem = (pagination.page - 1) * pagination.pageSize + 1;
  const endItem = Math.min(
    pagination.page * pagination.pageSize,
    pagination.total
  );

  return (
    <div className="p-4 sm:p-6 w-full max-w-none">
      <Card className="overflow-hidden border-none shadow-md w-full">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
          <div className="flex items-center gap-3">
            <Tag className="h-6 w-6" />
            <CardTitle className="text-lg">Gerenciamento de Tags</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <form
              onSubmit={handleFilter}
              className="flex flex-col sm:flex-row items-start gap-2 w-full sm:w-auto"
            >
              <div className="relative w-full sm:w-80">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  name="name"
                  className="pl-10 w-full border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                  placeholder="Nome da Tag"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                variant="outline"
                className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10 w-full sm:w-auto"
              >
                <Search className="h-4 w-4 mr-2" />
                Filtrar resultados
              </Button>
            </form>
            <Button
              onClick={() => handleNavigateToForm()}
              className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white w-full sm:w-auto"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Cadastrar Tag
            </Button>
          </div>

          {tags.length > 0 ? (
            <>
              <div className="border rounded-lg overflow-hidden w-full">
                {/* Versão para desktop */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          Nome da Tag
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">
                          Visualização
                        </th>
                        <th className="text-right py-3 px-4 font-semibold text-sm w-[180px]">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tags.map((tag: TagsOutput) => (
                        <tr key={tag.id} className="hover:bg-gray-50 border-t">
                          <td className="py-3 px-4 font-medium">{tag.name}</td>
                          <td className="py-3 px-4">
                            <Badge
                              style={{
                                backgroundColor:
                                  tag.styling?.primaryColor ?? "#E0E0E0",
                                color: tag.styling?.fontColor ?? "#000000",
                                border: `1px solid ${
                                  tag.styling?.secondaryColor ?? "#BDBDBD"
                                }`,
                              }}
                              className="rounded-md px-3 py-1 flex items-center gap-1 w-fit"
                            >
                              {tag.name}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                                onClick={() => handleNavigateToForm(tag.id)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Editar
                              </Button>

                              <Button
                                size="sm"
                                variant="destructive"
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => handleDeleteClick(tag.id)}
                              >
                                <Trash className="h-4 w-4 mr-1" />
                                Excluir
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Versão para mobile */}
                <div className="sm:hidden">
                  {tags.map((tag: TagsOutput) => (
                    <div key={tag.id} className="border-b p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="font-medium">{tag.name}</div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 border-primary text-primary hover:bg-primary/10"
                            onClick={() => handleNavigateToForm(tag.id)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDeleteClick(tag.id)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <Badge
                        style={{
                          backgroundColor:
                            tag.styling?.primaryColor ?? "#E0E0E0",
                          color: tag.styling?.fontColor ?? "#000000",
                          border: `1px solid ${
                            tag.styling?.secondaryColor ?? "#BDBDBD"
                          }`,
                        }}
                        className="rounded-md px-3 py-1 flex items-center gap-1 w-fit"
                      >
                        {tag.name}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Controles de paginação */}
              <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
                <div className="text-sm text-gray-500">
                  Mostrando {startItem} a {endItem} de {pagination.total} tags
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center mr-4">
                    <span className="text-sm text-gray-500 mr-2">
                      Itens por página:
                    </span>
                    <Select
                      value={pagination.pageSize.toString()}
                      onValueChange={handlePageSizeChange}
                    >
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue
                          placeholder={pagination.pageSize.toString()}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || isLoading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center gap-1 mx-2">
                      {/* Mostrar pelo menos a página 1 mesmo quando há apenas uma página */}
                      {pagination.pages === 0 ? (
                        <Button
                          variant="default"
                          size="icon"
                          className="h-8 w-8"
                        >
                          1
                        </Button>
                      ) : (
                        Array.from(
                          {
                            length: Math.max(1, Math.min(pagination.pages, 5)),
                          },
                          (_, i) => {
                            let pageNumber: number;

                            // Logic to show pages around current page
                            if (pagination.pages <= 5) {
                              pageNumber = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNumber = i + 1;
                            } else if (
                              pagination.page >=
                              pagination.pages - 2
                            ) {
                              pageNumber = pagination.pages - 4 + i;
                            } else {
                              pageNumber = pagination.page - 2 + i;
                            }

                            return (
                              <Button
                                key={pageNumber}
                                variant={
                                  pagination.page === pageNumber
                                    ? "default"
                                    : "outline"
                                }
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handlePageChange(pageNumber)}
                              >
                                {pageNumber}
                              </Button>
                            );
                          }
                        )
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={
                        pagination.page === pagination.pages || isLoading
                      }
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 border rounded-lg bg-gray-50 w-full">
              <Tag className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-base font-medium text-gray-700 mb-2">
                Nenhuma tag encontrada
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm
                  ? `Não encontramos tags com o termo "${searchTerm}"`
                  : "Você ainda não possui tags cadastradas"}
              </p>
              <Button
                onClick={() => handleNavigateToForm()}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Cadastrar Tag
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de confirmação de exclusão */}
      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão de Tag"
        message="Tem certeza que deseja excluir esta tag? Esta ação não pode ser desfeita."
        itemName={tags.find((t) => t.id === itemToDelete)?.name || "esta tag"}
      />
    </div>
  );
};

export default Tags;
